/**
 * Traduce el rechazo del backend a algo que pueda leer una persona.
 *
 * El backend responde con CÓDIGOS en MAYÚSCULAS_CON_BARRAS_BAJAS
 * (`ANNUAL_CONTRIBUTION_LIMIT_EXCEEDED`, `AMOUNT_BELOW_MINIMUM`…). Aquí se
 * convierten en el mensaje del toast: nunca se enseña el código crudo, y un
 * código desconocido cae en el genérico en vez de filtrar jerga del back.
 */

import { fmtEur } from './formatArcaNumbers';

interface TranslateOptions {
	/** Margen que le queda al usuario, para poder decirle el importe exacto. */
	remaining?: number;
}

const CODE_TO_KEY: Record<string, string> = {
	// No le queda margen: subir el importe no le va a servir de nada, así que
	// este mensaje NO puede ser el de "importe demasiado bajo".
	REMAINING_BELOW_MINIMUM_AMOUNT: 'individual.oneTimeContribution.errorNoMarginLeft',
	AMOUNT_BELOW_MINIMUM: 'individual.oneTimeContribution.errorBelowMinimum',
	ONE_TIME_CONTRIBUTION_ALREADY_MADE_TODAY: 'individual.oneTimeContribution.errorAlreadyToday',
	CASER_REJECTED_CONTRIBUTION: 'individual.oneTimeContribution.errorCaserRejected',
	USER_HAS_NO_PERSONAL_PLAN: 'individual.oneTimeContribution.errorNoPlan',
	// El front manda siempre el plan que se está viendo, así que esto solo salta
	// con VARIOS planes personales cuando el de esta vista todavía no tiene
	// `plan_catalog_id` (pendiente del backfill). No se le puede pedir al usuario
	// que reintente: no hay nada que pueda cambiar él.
	PLAN_CATALOG_ID_REQUIRED: 'individual.oneTimeContribution.errorMultiplePlans',
	USER_PLAN_NOT_ACTIVE_IN_ARCA: 'individual.oneTimeContribution.errorPlanNotActive',
	USER_STATUS_NOT_ACTIVE: 'individual.oneTimeContribution.errorNotAvailable'
};

export function translateOneTimeContributionError(
	error: unknown,
	translate: (key: string) => string,
	options: TranslateOptions = {}
): string {
	const code = error instanceof Error ? error.message : '';

	// El tope superado se trata aparte: si sabemos cuánto le queda, se le dice,
	// que es la diferencia entre "no puedes" y "puedes hasta 250 €".
	if (code === 'ANNUAL_CONTRIBUTION_LIMIT_EXCEEDED') {
		return options.remaining === undefined
			? translate('individual.oneTimeContribution.errorLimitExceededNoAmount')
			: `${translate('individual.oneTimeContribution.errorLimitExceeded')} ${fmtEur(options.remaining)}`;
	}

	return translate(CODE_TO_KEY[code] ?? 'individual.oneTimeContribution.errorGeneric');
}
